import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Stack, Alert, Typography } from "@mui/material";

import AssessmentIcon from "@mui/icons-material/Assessment";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SendIcon from "@mui/icons-material/Send";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import StatusChip from "../components/common/StatusChip";
import ConfirmDialog from "../components/common/ConfirmDialog";
import RowActionMenu, {
  RowActionDef,
} from "../components/common/RowActionMenu";

import BranchSelectFilter from "../components/common/BranchSelectFilter";
import AcademicYearSelectFilter from "../components/common/AcademicYearSelectFilter";
import TermSelectFilter from "../components/common/TermSelectFilter";
import ClassroomSelectFilter from "../components/common/ClassroomSelectFilter";
import StudentSearchSelect from "../components/common/StudentSearchSelect";

import ViewStudentTermReportModal from "../components/studentTermReports/ViewStudentTermReportModal";
import UpdateStudentTermReportModal from "../components/studentTermReports/UpdateStudentTermReportModal";
import GenerateReportsModal from "../components/studentTermReports/GenerateReportsModal";

import useDebounce from "../hooks/useDebounce";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { usePermissions } from "../hooks/usePermissions";
import {
  getStudentsTermReports,
  getStudentTermReportById,
  dispatchReportsToClassroom,
  resetReportServerStatus,
} from "../redux/actions/studentTermReportActions";

import { StudentTermReportResponseDto } from "../shared/dtos/studentTermReport/StudentTermReportResponseDto";

const StudentsTermReportsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // Redux State
  const {
    reports,
    pagination,
    loadingReports,
    errorLoadingReports,
    serverStatus,
    report,
    loadingReport,
    loadingUpdate,
    errorUpdating,
  } = useAppSelector((state) => state.studentTermReport);

  const { userInfo } = useAppSelector((state) => state.auth);

  // Filters State
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [selectedAcademicYearId, setSelectedAcademicYearId] =
    useState<string>("");
  const [selectedTermId, setSelectedTermId] = useState<string>("");
  const [selectedClassroomId, setSelectedClassroomId] = useState<string>("");
  const [selectedStudentId, setSelectedStudentId] = useState<string>("");

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 15 });

  const [selectedReport, setSelectedReport] =
    useState<StudentTermReportResponseDto | null>(null);

  // Modal State
  const [isGenerateOpen, setIsGenerateOpen] = useState<boolean>(false);
  const [isViewOpen, setIsViewOpen] = useState<boolean>(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState<boolean>(false);
  const [isDispatchConfirmOpen, setIsDispatchConfirmOpen] =
    useState<boolean>(false);

  const canCreate = hasPermission("term_report:create");
  const canDispatch =
    hasPermission("sms_outbox:send") || hasPermission("term_report:update");

  // Only trigger query if both required scope filters (Classroom & Term) are present
  const hasRequiredFilters = Boolean(selectedClassroomId && selectedTermId);

  const fetchReports = useCallback((): void => {
    if (!hasRequiredFilters) return;

    dispatch(
      getStudentsTermReports({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch.trim() || undefined,
        branchId: selectedBranchId || undefined,
        academicYearId: selectedAcademicYearId || undefined,
        termId: selectedTermId,
        classroomId: selectedClassroomId,
        studentId: selectedStudentId || undefined,
      }),
    );
  }, [
    dispatch,
    hasRequiredFilters,
    pageConfig,
    debouncedSearch,
    selectedBranchId,
    selectedAcademicYearId,
    selectedTermId,
    selectedClassroomId,
    selectedStudentId,
  ]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Reset pagination on filter change
  useEffect(() => {
    setPageConfig((p) => ({ ...p, page: 1 }));
  }, [
    selectedBranchId,
    selectedAcademicYearId,
    selectedTermId,
    selectedClassroomId,
    selectedStudentId,
    debouncedSearch,
  ]);

  // Server side-effects handling
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "generate" && status === 200) {
      setIsGenerateOpen(false);
      fetchReports();
    }
    if (operation === "dispatch" && status === 202) {
      setIsDispatchConfirmOpen(false);
    }
    if (operation === "update" && status === 200) {
      setIsUpdateOpen(false);
    }
    dispatch(resetReportServerStatus());
  }, [serverStatus, dispatch, fetchReports]);

  const handleConfirmDispatch = (): void => {
    dispatch(
      dispatchReportsToClassroom({
        classroomId: selectedClassroomId,
        termId: selectedTermId,
      }),
    );
  };

  const handleOpenView = (
    selectedReport: StudentTermReportResponseDto,
  ): void => {
    dispatch(getStudentTermReportById(selectedReport.id));
    setIsViewOpen(true);
  };

  const handleOpenUpdate = (
    selectedReport: StudentTermReportResponseDto,
  ): void => {
    setSelectedReport(selectedReport);
    setIsUpdateOpen(true);
  };

  const columns = useMemo<TableColumn<StudentTermReportResponseDto>[]>(
    () => [
      { id: "classPosition", label: "Pos", minWidth: 60 },
      {
        id: "studentName",
        label: "Student Name",
        minWidth: 200,
        format: (v) => (
          <Typography sx={{ fontWeight: 600 }}>{String(v)}</Typography>
        ),
      },
      { id: "totalMarks", label: "Total", minWidth: 100 },
      {
        id: "averageMarks",
        label: "Avg (%)",
        minWidth: 100,
        format: (v) => `${v}%`,
      },
      {
        id: "meanGrade",
        label: "Grade",
        minWidth: 80,
        format: (v) => <StatusChip status={v as any} />,
      },
      {
        id: "isPublished",
        label: "Status",
        minWidth: 100,
        format: (v) => (
          <StatusChip status={v ? ("PUBLISHED" as any) : ("DRAFT" as any)} />
        ),
      },
    ],
    [],
  );

  const renderRowActions = (
    row: StudentTermReportResponseDto,
  ): React.ReactNode => {
    const inlineActions: RowActionDef[] = [
      {
        label: "View",
        icon: <VisibilityOutlinedIcon fontSize="small" />,
        color: "info",
        onClick: () => handleOpenView(row),
        show: true,
      },
    ];

    const menuActions: RowActionDef[] = [
      {
        label: "Update remarks",
        icon: <EditOutlinedIcon fontSize="small" />,
        onClick: () => handleOpenUpdate(row),
        show: canCreate,
      },
    ];

    return (
      <RowActionMenu inlineActions={inlineActions} menuActions={menuActions} />
    );
  };

  return (
    <PageContainer
      title="Students Term Reports"
      subtitle="Manage rankings, individual marks, and published academic reports."
      actions={
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          {canCreate && (
            <AppButton
              variant="contained"
              color="primary"
              startIcon={<AssessmentIcon />}
              onClick={() => setIsGenerateOpen(true)}
              disabled={!hasRequiredFilters}
            >
              Generate
            </AppButton>
          )}
          {canDispatch && (
            <AppButton
              variant="contained"
              color="secondary"
              startIcon={<SendIcon />}
              onClick={() => setIsDispatchConfirmOpen(true)}
              disabled={!hasRequiredFilters || !reports?.length}
            >
              Dispatch All
            </AppButton>
          )}
        </Stack>
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search student by name or ID..."
      >
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={setSelectedBranchId}
        />

        <AcademicYearSelectFilter
          value={selectedAcademicYearId}
          onChange={setSelectedAcademicYearId}
        />

        <TermSelectFilter value={selectedTermId} onChange={setSelectedTermId} />

        <ClassroomSelectFilter
          value={selectedClassroomId}
          branchId={selectedBranchId}
          onChange={setSelectedClassroomId}
        />

        <StudentSearchSelect
          value={selectedStudentId}
          branchId={selectedBranchId}
          onChange={setSelectedStudentId}
        />
      </PageToolbar>

      {/* RENDER TABLE OR INSTRUCTION ALERT */}
      {!hasRequiredFilters ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Please select both a <b>Classroom</b> and a <b>Term</b> from the
          filters above to load performance reports.
        </Alert>
      ) : (
        <StandardTable<StudentTermReportResponseDto>
          columns={columns}
          data={reports}
          loading={loadingReports}
          error={errorLoadingReports}
          onRetry={fetchReports}
          count={pagination?.totalElements || 0}
          page={pageConfig.page}
          rowsPerPage={pageConfig.limit}
          onPageChange={(p) => setPageConfig((prev) => ({ ...prev, page: p }))}
          onRowsPerPageChange={(l) =>
            setPageConfig((prev) => ({ ...prev, limit: l, page: 1 }))
          }
          emptyMessage="No performance reports generated yet for this class and term"
          rowAction={renderRowActions}
        />
      )}

      {/* GENERATE MODAL */}
      {isGenerateOpen && (
        <GenerateReportsModal
          open={isGenerateOpen}
          onClose={() => setIsGenerateOpen(false)}
          filters={{
            classroomId: selectedClassroomId,
            termId: selectedTermId,
          }}
          branchId={selectedBranchId || userInfo?.branchId}
          loading={false}
          error={null}
        />
      )}

      {/* VIEW MODAL */}
      {isViewOpen && (
        <ViewStudentTermReportModal
          open={isViewOpen}
          onClose={() => setIsViewOpen(false)}
          report={report}
          loading={loadingReport}
        />
      )}

      {/* UPDATE MODAL */}
      {isUpdateOpen && selectedReport && (
        <UpdateStudentTermReportModal
          open={isUpdateOpen}
          onClose={() => setIsUpdateOpen(false)}
          report={selectedReport}
          loading={loadingUpdate}
          error={errorUpdating}
        />
      )}

      {/* DISPATCH CONFIRM DIALOG */}
      {isDispatchConfirmOpen && (
        <ConfirmDialog
          open={isDispatchConfirmOpen}
          onClose={() => setIsDispatchConfirmOpen(false)}
          onConfirm={handleConfirmDispatch}
          loading={false}
          title="Dispatch Academic Reports"
          description={`Are you sure you want to send branded PDF report cards via Email and SMS to parents of all ${reports?.length || 0} students in this class?`}
        />
      )}
    </PageContainer>
  );
};

export default StudentsTermReportsPage;
